'use client'

import { atom } from 'jotai'
import Cookies from 'js-cookie'

function getJwtExpiration(token: string): Date | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null

    const payload = JSON.parse(atob(parts[1]))
    if (payload.exp) {
      return new Date(payload.exp * 1000) // JWT exp is in seconds, convert to milliseconds
    }
    return null
  } catch {
    return null
  }
}

interface AtomWithCookieOptions<T> {
  toCookieValue?: (value: T) => unknown
}

export function atomWithCookie<T = unknown>(
  key: string,
  initial: T,
  options?: AtomWithCookieOptions<T>
) {
  const baseAtom = atom<T>(initial)

  const derivedAtom = atom(
    (get) => {
      const atomValue = get(baseAtom)
      const cookieValue = Cookies.get(key)

      // if cookie exists, use cookie value
      if (cookieValue !== undefined) {
        try {
          return JSON.parse(cookieValue) as T
        } catch {
          return cookieValue as T
        }
      }

      return atomValue
    },
    (_, set, newValue: T) => {
      // update cookie
      const isProduction = process.env.NODE_ENV === 'production'
      const isLocalhost = typeof window !== 'undefined' && window.location.hostname === 'localhost'

      if (newValue === null || newValue === undefined) {
        Cookies.remove(key)
        set(baseAtom, newValue)
        return
      }

      const cookieValue = options?.toCookieValue ? options.toCookieValue(newValue) : newValue
      const stringValue = JSON.stringify(cookieValue)

      // --- calculate expiration ---
      const expiration = getJwtExpiration(stringValue as string) || 7 // Default 7 days

      // --- secure option: false in localhost ---
      const cookieOptions: Cookies.CookieAttributes = {
        secure: isProduction && !isLocalhost,
        sameSite: 'lax',
        expires: expiration,
        path: '/',
      }

      // --- AUTH_STATE ---
      if (key.includes('auth-state') || key.includes('auth_state')) {
        cookieOptions.expires = 7
      }

      Cookies.set(key, stringValue as string, cookieOptions)

      set(baseAtom, newValue)
    }
  )

  return derivedAtom
}
